import React from 'react';

import OmInputFieldWrapper from '../forms/OmInputFieldWrapper';
import { createActionHandler, roundCurrency } from '../../util';

import ResultCard from './ResultCardSection';

import {
    setShortTermGoalOnceOffValue,
    setMonthlyValue,
    calculateShortTermGoal,
    setInvestmentTenureValue,
    setPotentialLossPercentageValue,
} from '@om/redux/actions/investmentCalculator/shortTermGoal';

import OmDropdownField from '../forms/OmDropdownField';

import { useDispatch, useSelector } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import {
    getAmountOnceOffValue,
    getMonthlyAmountValue,
    getCalculateDisabled,
    getInvestmentTenureValue,
    getPotentialLossPercentageValue,
} from '@om/redux/selectors/investmentCalculator/shortTermGoalCalculator';

import { useBindCustomEvent } from '../../hooks/useBindCustomEvent';

import {
    getBalancedFundValue,
    getEquityFundValue,
    getIsEvenSplit,
    getSplitError,
    getTotalPercentage,
    getEquityFundSplitValue,
    getBalancedFundSplitValue,
} from '@om/redux/selectors/investmentCalculator/shortTermGoalSplit';

import {
    deselectEvenSplit,
    selectEvenSplit,
    setBalancedFundValue,
    setEquityFundValue,
} from '@om/redux/actions/investmentCalculator/shortTermGoalSplit';

import {
    getShowShortTermGoalSplitForm,
    getShowShortTermGoalDropField,
    getShowRecommendation,
} from '@om/redux/selectors/investmentCalculator/investmentCalculator';

import {
    OmInvestmentCalcSection,
    OmApplicationFundSplitList,
    OmApplicationFundSplitCard,
} from '@om/component-library-react';

import TwoColumnImageLeft from '../TwoColumnImageLeft';

const investmentTenureOptions = [
    { name: '9', description: '1 month' },
    { name: '10', description: '6 months' },
    { name: '11', description: '1 year' },
    { name: '12', description: '2 years' },
    { name: '13', description: '3 years' },
    { name: '14', description: 'More than 3 years' },
];

const potentialLossPercentages = [
    { name: '0', description: '0%' },
    { name: '0.05', description: '5%' },
    { name: '0.1', description: '10%' },
    { name: '>0.1', description: 'More than 10%' },
];

const shouldDisplayAddFundsButton = false;
const shouldDisplayDeleteIcon = false;
const fundOne = false;

const ShortTermGoalCalculator = ({ slot, twoColumnImageLeftContent }) => {
    const dispatch = useDispatch();

    const {
        amountOnceOff,
        monthlyAmount,
        investmentTenure,
        potentialLossPercentage,
        calculateButtonDisabled,
        showSplitForm,
        showDropField,
        balancedFund,
        equityFund,
        isEvenSplit,
        splitError,
        totalPercentage,
        equitySplitValue,
        balancedSplitValue,
        showRecommendation,
    } = useSelector(
        createStructuredSelector({
            amountOnceOff: getAmountOnceOffValue,
            monthlyAmount: getMonthlyAmountValue,
            investmentTenure: getInvestmentTenureValue,
            potentialLossPercentage: getPotentialLossPercentageValue,
            calculateButtonDisabled: getCalculateDisabled,
            showSplitForm: getShowShortTermGoalSplitForm,
            showDropField: getShowShortTermGoalDropField,
            balancedFund: getBalancedFundValue,
            equityFund: getEquityFundValue,
            isEvenSplit: getIsEvenSplit,
            splitError: getSplitError,
            totalPercentage: getTotalPercentage,
            equitySplitValue: getEquityFundSplitValue,
            balancedSplitValue: getBalancedFundSplitValue,
            showRecommendation: getShowRecommendation,
        }),
    );

    const customActionHandler = (dispatch, action, correspondingAction) => {
        if (!dispatch || !action) {
            return () => {};
        }

        return value => {
            //Set the corresponding fund value to 100 - x
            dispatch(correspondingAction((100 - Number(value)).toString()));
            //Set the fund value that is currently being edited
            dispatch(action(value.toString()));
            if (value !== 50) dispatch(deselectEvenSplit());
            else dispatch(selectEvenSplit());
        };
    };

    const ref = useBindCustomEvent('checkBoxChanged', () => {
        if (isEvenSplit) {
            dispatch(deselectEvenSplit());
            // dispatch(discardEvenSplit());
            return;
        }
        dispatch(selectEvenSplit());
    });

    const equityRef = useBindCustomEvent('onDelete', () => {
        dispatch(setEquityFundValue('0'));
        dispatch(setBalancedFundValue('100'));
    });

    const balancedRef = useBindCustomEvent('onDelete', () => {
        dispatch(setBalancedFundValue('0'));
        dispatch(setEquityFundValue('100'));
    });

    function updateInvestmentTenure(investmentTenure) {
        dispatch(setInvestmentTenureValue(investmentTenure.detail.id));
    }

    function updatePotentialLossPercentage(potentialLossPercentage) {
        dispatch(
            setPotentialLossPercentageValue(potentialLossPercentage.detail.id),
        );
    }

    return (
        <OmInvestmentCalcSection slot={slot}>
            <div slot='form-content'>
                <h4>Invest for a Short Term Goal</h4>
                <h6 className='no-bottom-margin'>
                    How much do you want to invest once-off?
                </h6>
                <OmInputFieldWrapper
                    isSensitive={true}
                    preset='KES'
                    prefixString='KES'
                    required={true}
                    size='full'
                    formId='ShortTermGoalCalculatorForm'
                    name='amountOnceOff'
                    mask='thousands-spaced'
                    keyboardMode='numeric'
                    hintText=''
                    reduxValue={amountOnceOff}
                    persistHandler={createActionHandler(
                        dispatch,
                        setShortTermGoalOnceOffValue,
                    )}
                />
                <h6 className='no-bottom-margin'>
                    How much do you want to save monthly?
                </h6>
                <OmInputFieldWrapper
                    isSensitive={true}
                    preset='KES'
                    prefixString='KES'
                    required={true}
                    size='full'
                    formId='ShortTermGoalCalculatorForm'
                    name='amountMonthly'
                    mask='thousands-spaced'
                    keyboardMode='numeric'
                    hintText=''
                    align='left'
                    reduxValue={monthlyAmount}
                    persistHandler={createActionHandler(
                        dispatch,
                        setMonthlyValue,
                    )}
                />
                <h6 className='no-bottom-margin'>
                    Please note that this is non-contractual
                </h6>
                <h6 className='no-bottom-margin'>
                    How long do you want to invest your money?
                </h6>
                <OmDropdownField
                    useNativeMobileDropdown='true'
                    placeholder=''
                    required='true'
                    formId='ShortTermGoalCalculatorForm'
                    name='investmentTenure'
                    align='left'
                    reduxValue={investmentTenure}
                    errorMessage={investmentTenure}
                    onSelectHandler={updateInvestmentTenure}
                    options={
                        investmentTenureOptions &&
                        investmentTenureOptions.map(option => ({
                            value: option.name,
                            label: option.description,
                        }))
                    }
                />
                {showDropField ? (
                    <div>
                        <h6 className='no-bottom-margin'>
                            How much can you afford to lose in a year?
                        </h6>
                        <OmDropdownField
                            useNativeMobileDropdown='true'
                            placeholder=''
                            required='true'
                            formId='ShortTermGoalCalculatorForm'
                            name='potentialLossPercentage'
                            align='left'
                            reduxValue={potentialLossPercentage}
                            errorMessage={potentialLossPercentage}
                            onSelectHandler={updatePotentialLossPercentage}
                            options={
                                potentialLossPercentages &&
                                potentialLossPercentages.map(option => ({
                                    value: option.name,
                                    label: option.description,
                                }))
                            }
                        />{' '}
                    </div>
                ) : null}

                {showSplitForm ? (
                    <div className='split-form'>
                        <h6 className='no-bottom-margin'>
                            Split your <strong>investment between funds</strong>
                        </h6>
                        <OmApplicationFundSplitList
                            eevenSplit={isEvenSplit}
                            totalPercentage={totalPercentage}
                            showAddMoreFundsButton={shouldDisplayAddFundsButton}
                            totalValue={
                                'KES ' +
                                roundCurrency(
                                    balancedSplitValue + equitySplitValue,
                                )
                            }>
                            <div slot='fund-cards'>
                                <OmApplicationFundSplitCard
                                    fundName={'Equity Fund'}
                                    value={
                                        'KES ' + roundCurrency(equitySplitValue)
                                    }
                                    hideDeleteButton={shouldDisplayDeleteIcon}>
                                    <OmInputFieldWrapper
                                        align='left'
                                        type={'number'}
                                        maxLength={3}
                                        slot='percentage-input'
                                        required={true}
                                        size='small'
                                        formId='ShortTermGoalCalculatorForm'
                                        name='equityFund'
                                        keyboardMode='numeric'
                                        hintText=''
                                        reduxValue={equityFund}
                                        persistHandler={customActionHandler(
                                            dispatch,
                                            setEquityFundValue,
                                            setBalancedFundValue,
                                        )}
                                    />
                                </OmApplicationFundSplitCard>
                                <OmApplicationFundSplitCard
                                    fundName={'Balanced Fund'}
                                    errorMessage={splitError}
                                    value={
                                        'KES ' +
                                        roundCurrency(balancedSplitValue)
                                    }
                                    hideDeleteButton={shouldDisplayDeleteIcon}
                                    fundOne={fundOne}>
                                    <OmInputFieldWrapper
                                        align='left'
                                        type={'number'}
                                        maxLength={3}
                                        slot='percentage-input'
                                        required={true}
                                        size='small'
                                        formId='ShortTermGoalCalculatorForm'
                                        name='balancedFund'
                                        keyboardMode='numeric'
                                        hintText=''
                                        reduxValue={balancedFund}
                                        persistHandler={customActionHandler(
                                            dispatch,
                                            setBalancedFundValue,
                                            setEquityFundValue,
                                        )}
                                    />
                                </OmApplicationFundSplitCard>
                            </div>
                        </OmApplicationFundSplitList>
                    </div>
                ) : null}
            </div>
            <ResultCard
                calculateButtonDisabled={calculateButtonDisabled}
                setInvestmentResult={() => dispatch(calculateShortTermGoal())}
            />

            <div slot='product-offering'>
                {showRecommendation && (
                    <TwoColumnImageLeft
                        twoColumnImageLeftContent={twoColumnImageLeftContent}
                    />
                )}
            </div>
        </OmInvestmentCalcSection>
    );
};

export default ShortTermGoalCalculator;
